import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { apiRequest } from "@/lib/queryClient";
import { openPaddleCheckout } from "@/lib/paddleCheckout";
import { paddlePaymentLinkTransaction } from "@/lib/paddlePaymentLink";
import type { PaddlePaymentLink } from "@shared/paddle-api";

type Notice = { tone: "pending" | "error"; text: string };
export function usePaddlePaymentLink(userId: string | null) {
  const [entry] = useState(() => ({ present: window.location.pathname === "/checkout",
    transactionId: paddlePaymentLinkTransaction(window.location.search) }));
  const nativeIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  const [notice, setNotice] = useState<Notice | null>(null);
  const opened = useRef<string | null>(null);
  const client = useQueryClient();
  const resolved = useQuery<PaddlePaymentLink>({
    queryKey: ["/api/paddle/payment-link", userId, entry.transactionId],
    enabled: entry.present && !!userId && !!entry.transactionId && !nativeIos,
    queryFn: async () => (await apiRequest(`/api/paddle/payment-link/${entry.transactionId}`, { method: "GET" })).json(),
    retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false,
    staleTime: Infinity, gcTime: 0,
  });
  useEffect(() => {
    const result = resolved.data;
    const key = `${userId}:${entry.transactionId}`;
    if (!userId || !result || result.action !== "checkout" || opened.current === key) return;
    let active = true;
    let confirmed = false;
    setNotice({ tone: "pending", text: "Opening your verified existing payment link. No second purchase was created." });
    void openPaddleCheckout(result.bootstrap, event => {
      if (!active || confirmed || !["checkout.completed", "checkout.closed"].includes(event.name ?? "") ||
          (event.data?.transaction_id && event.data.transaction_id !== result.bootstrap.transactionId)) return;
      confirmed = true;
      const endpoint = result.kind === "checkout" ? "/api/billing/checkout/confirm" : "/api/paddle/reconcile";
      void apiRequest(endpoint, { method: "POST", ...(result.kind === "checkout"
        ? { body: { transactionId: result.bootstrap.transactionId } } : {}) })
        .then(async () => {
          if (active) setNotice({ tone: "pending", text: "Payment status refreshed. Review your confirmed subscription below." });
          await Promise.all(["/api/billing/subscription", "/api/agent/status", "/api/auth/user"]
            .map(key => client.invalidateQueries({ queryKey: [key] })));
        })
        .catch(() => { if (active) setNotice({ tone: "error",
          text: "Payment is not confirmed yet. Refresh billing status below; do not submit another purchase." }); });
    }, () => {
      if (!active || opened.current === key) return false;
      opened.current = key;
      return true;
    }).catch(() => {
      if (active) setNotice({ tone: "error", text: "Checkout could not be opened. Review Account or contact support." });
    });
    return () => { active = false; };
  }, [userId, entry.transactionId, resolved.data, client]);
  if (!entry.present || !userId) return null;
  if (!entry.transactionId) return { tone: "error" as const, text: "This payment link is invalid. Use the billing controls below or contact support." };
  if (nativeIos) return { tone: "error" as const, text: "Open this payment link in your web browser to manage billing." };
  if (resolved.isError) return { tone: "error" as const, text: "This payment link could not be verified for your account. No purchase was started. Review Account or contact support." };
  if (resolved.data?.action === "account") return { tone: "pending" as const,
    text: "Review your billing status below. Use the Account controls to manage payment details; this link did not start another purchase." };
  return notice ?? { tone: "pending" as const, text: "Verifying your payment link…" };
}
